import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import i18next from 'i18next';
import zhConvertor from 'zhconvertor';
import SeatBoundaryMap from './SeatBoundaryMap';
import { recordSeat } from '../../utils/interestTracker';
import { isBeforeTarget } from '../../utils/electionTime';
import PinnedArticles from '../PinnedArticles/PinnedArticles';
import './SwingSeats.css';

// Chinese is stored traditional (繁體) and converted to simplified by tr() unless
// the site language is zh_TW — matching SeatInfoPanel / negerisembilan.jsx.
// Seat and article names coming from the API are already converted server-side,
// so they must NOT go through tr() again.
const tr = (text) =>
  localStorage.getItem('lang') !== 'zh_TW' ? zhConvertor.t2s(text) : text;

// The API returns romanised names uppercase ("SERI MENANTI"); cards show title case.
const titleCase = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/(^|[\s'\-/])(\p{L})/gu, (_, sep, ch) => sep + ch.toUpperCase());

// Editor logos take precedence; fall back to the party-icon convention URL.
const allianceIcon = (alliance_en, alliance_logo, party_logo) =>
  alliance_logo ||
  (alliance_en ? `https://sabah2025.orientaldaily.com.my/party/${alliance_en}.png` : '') ||
  party_logo || '';

// Independent (BEBAS) candidates show their dedicated logo in place of the
// alliance/party icon; otherwise fall back to the regular icon chain.
const seatIcon = (c) =>
  c && (c.party_eng || '').toUpperCase() === 'BEBAS' && c.bebas_logo
    ? c.bebas_logo
    : allianceIcon(c?.alliance_en, c?.alliance_logo, c?.party_logo);

// Before voting day the map tile shows the previous holder's alliance colour;
// once countdownTarget passes it switches to the live current winner/leader
// (grey until results come in). Gate shared via utils/electionTime.

const winnerOf = (seat) =>
  seat && Array.isArray(seat.candidate)
    ? seat.candidate.find((c) => parseInt(c.status, 10) === 1)
    : null;

// Leading candidate for an in-progress current election (no winner yet): highest
// votes, only when some votes have been counted.
const leaderOf = (seat) => {
  if (!seat || !Array.isArray(seat.candidate) || seat.candidate.length === 0) return null;
  if (parseInt(seat.total_votes, 10) <= 0) return null;
  return seat.candidate.reduce((best, c) =>
    (parseInt(c.votes, 10) || 0) > (parseInt(best.votes, 10) || 0) ? c : best,
  );
};

// Candidate name / party text inside a row-body opens the related-news panel
// for that term (a name falls back to the party when it has no articles,
// matching CandidateBar). stopPropagation keeps the card's own STATE_TAG
// click from also firing.
const TagText = ({ className, text, fallbackTag, onTagClick }) => {
  if (!text) return null;
  const open = (e) => {
    e.stopPropagation();
    if (onTagClick) onTagClick({ tag: text, fallbackTag, location: 'swing_seats' });
  };
  return (
    <span
      className={className}
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open(e);
        }
      }}
    >
      {text}
    </span>
  );
};

const SwingSeatCard = ({ seat, past, current, onClick, onTagClick }) => {
  const pastWinner = winnerOf(past);
  const pastColor = pastWinner ? (pastWinner.alliance_color || '#8c8c8c') : undefined;

  const currentWinner = winnerOf(current);
  const currentLeader = currentWinner || leaderOf(current);
  const currentColor = currentLeader
    ? (currentLeader.alliance_color || '#8c8c8c')
    : undefined;
  // Past holder before voting day, live leader after.
  const tileColor = isBeforeTarget() ? pastColor : currentColor;
  const hasCurrent = current && parseInt(current.total_votes, 10) > 0;
  const currentCandidates =
    current && Array.isArray(current.candidate) ? current.candidate : [];

  return (
    <div
      className="swing-seat-card"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="swing-seat-card__map">
        <SeatBoundaryMap seatCode={seat.code} fill={tileColor} />
        <span className="swing-seat-card__code">{seat.code}</span>
      </div>

      <div className="swing-seat-card__head">
        <span className="swing-seat-card__name">{seat.c}</span>
        <span className="swing-seat-card__ename">{seat.e}</span>
      </div>

      {window.hideCurrentContestant === false && 
      <div className="swing-seat-card__row swing-seat-card__row--current">
        <span className="swing-seat-card__row-label">
          {!hasCurrent && currentCandidates.length > 0 ? tr('本届候选人') : tr('本届')}
        </span>
        {hasCurrent && currentLeader ? (
          <span className="swing-seat-card__row-body">
            {seatIcon(currentLeader) && (
              <img
                className="swing-seat-card__icon"
                src={seatIcon(currentLeader)}
                alt=""
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            <TagText
              className="swing-seat-card__cand"
              text={currentLeader.candidate_name}
              fallbackTag={currentLeader.party}
              onTagClick={onTagClick}
            />
            <TagText
              className="swing-seat-card__party"
              text={currentLeader.party}
              onTagClick={onTagClick}
            />
            {!currentWinner && (
              <span className="swing-seat-card__leading">{tr('暂时领先')}</span>
            )}
          </span>
        ) : currentCandidates.length > 0 ? (
          <span className="swing-seat-card__candlist">
            {currentCandidates.map((c, i) => (
              <span key={i} className="swing-seat-card__row-body">
                {seatIcon(c) && (
                  <img
                    className="swing-seat-card__icon"
                    src={seatIcon(c)}
                    alt=""
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                <TagText
                  className="swing-seat-card__cand"
                  text={c.candidate_name}
                  fallbackTag={c.party}
                  onTagClick={onTagClick}
                />
                <TagText
                  className="swing-seat-card__party"
                  text={c.party}
                  onTagClick={onTagClick}
                />
              </span>
            ))}
          </span>
        ) : (
          <span className="swing-seat-card__muted">{tr('尚未开始')}</span>
        )}
      </div>}

      <div className="swing-seat-card__row swing-seat-card__row--past">
        <span className="swing-seat-card__row-label">{tr('上届')}</span>
        {pastWinner ? (
          <span className="swing-seat-card__row-body">
            {seatIcon(pastWinner) && (
              <img
                className="swing-seat-card__icon"
                src={seatIcon(pastWinner)}
                alt=""
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            <TagText
              className="swing-seat-card__cand"
              text={pastWinner.candidate_name}
              fallbackTag={pastWinner.party}
              onTagClick={onTagClick}
            />
            <TagText
              className="swing-seat-card__party"
              text={pastWinner.party}
              onTagClick={onTagClick}
            />
          </span>
        ) : (
          <span className="swing-seat-card__muted">{tr('暂无资料')}</span>
        )}
        {pastWinner && parseInt(past.majority, 10) > 0 && (
          <span className="swing-seat-card__majority">
            {tr('多数票')} {parseInt(past.majority, 10).toLocaleString()}
          </span>
        )}
      </div>

      {/* Card is a role="button" opening the seat panel — PinnedArticles stops
          propagation on its own links. */}
      <PinnedArticles
        pins={seat.pins}
        className="swing-seat-card__news"
        seat={`Negerisembilan ${seat.code}`}
        location="swing_seats"
        utmSource="sseat"
      />

      <Link
        className="swing-seat-card__button"
        to={`/seats/negerisembilan/n#s${seat.code}`}
        onClick={(e) => {
          e.stopPropagation();
          recordSeat({ slug: 'negerisembilan', seatname: seat.code, label: seat.c });
        }}
      >
        {tr('查看该议席')} →
      </Link>
    </div>
  );
};

// Both datasets come from Home's shared seatinfo fetches (current 15s poll +
// one-time last_election) — this section no longer issues its own requests.
const toMap = (rows) => {
  const out = {};
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (row && row.seatname) out[row.seatname] = row;
  });
  return out;
};

// onSeatClick receives the clicked seat's code (e.g. "N7") to open its seat
// info panel; onTagClick opens the related-news panel for candidate/party
// name text inside a card.
const SwingSeatsSection = ({ current, past, onSeatClick, onTagClick }) => {
  // seatname (e.g. "N7") -> seat record, for current and past elections.
  const currentBySeat = useMemo(() => toMap(current), [current]);
  const pastBySeat = useMemo(() => toMap(past), [past]);

  // 焦点选区 are flagged per seat by the editors (edistrict.is_focus) and arrive
  // on the live seatinfo poll — no hardcoded list. `ednamec` and the pin titles
  // are already in the site language server-side, so they skip tr().
  const focusSeats = useMemo(
    () =>
      (Array.isArray(current) ? current : [])
        .filter((row) => row && row.focus && row.seatname)
        .map((row) => ({
          code: row.seatname,
          c: row.ednamec || row.seatname,
          e: titleCase(row.edname),
          order: Number(row.focus_order) || 0,
          pins: Array.isArray(row.pins) ? row.pins : [],
        }))
        .sort(
          (a, b) =>
            a.order - b.order || a.code.localeCompare(b.code, undefined, { numeric: true }),
        ),
    [current],
  );

  // Desktop-only left/right paging arrows, mirroring VideoSection.
  const scrollerRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const updateArrows = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 1);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
    // Cards arrive with the first poll, after mount — re-measure when the count changes.
  }, [updateArrows, focusSeats.length]);

  const scrollByPage = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  // Nothing flagged (or the first poll hasn't landed) — hide the section rather
  // than leave a bare heading.
  if (focusSeats.length === 0) return null;

  return (
    <section className="swing-seats" aria-labelledby="swing-seats-heading">
      <h2 id="swing-seats-heading" className="section-title">
        {i18next.t('焦点选区') || '焦点选区'}
      </h2>
      <div className="swing-seats__viewport">
        <button
          type="button"
          className="swing-seats__nav swing-seats__nav--prev"
          onClick={() => scrollByPage(-1)}
          disabled={!canPrev}
          aria-label={i18next.t('上一个')}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <polyline points="15,5 8,12 15,19" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className="swing-seats__scroller" ref={scrollerRef}>
          {focusSeats.map((seat) => (
            <SwingSeatCard
              key={seat.code}
              seat={seat}
              past={pastBySeat[seat.code]}
              current={currentBySeat[seat.code]}
              onClick={() => onSeatClick && onSeatClick(seat.code)}
              onTagClick={onTagClick}
            />
          ))}
        </div>
        <button
          type="button"
          className="swing-seats__nav swing-seats__nav--next"
          onClick={() => scrollByPage(1)}
          disabled={!canNext}
          aria-label={i18next.t('下一个')}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <polyline points="9,5 16,12 9,19" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </section>
  );
};

export default SwingSeatsSection;
